import React, { useRef } from 'react';
import Appear from '../components/ui/Appear';
import useUserStore from '../stores/useUserStore';
import type { PresenceUser } from './types';

interface PresenceRowProps {
  presence: PresenceUser[];
}

const MAX = 5;

const sameId = (a: unknown, b: unknown) => a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

const nameOf = (p: PresenceUser) => [p.firstname, p.lastname].filter(Boolean).join(' ') || 'Someone';

const initialsOf = (p: PresenceUser) => `${p.firstname?.[0] ?? ''}${p.lastname?.[0] ?? ''}`.toUpperCase() || '?';

const AVATAR =
  'flex h-5 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-semibold leading-none ring-2 ring-surface';

/**
 * Who else has this plan open: overlapping initials, the current person first. Renders
 * nothing while the only one here is the person looking.
 */
const PresenceRow: React.FC<PresenceRowProps> = ({ presence }) => {
  const userId = useUserStore((s: any) => s.user?.id);
  // The people here at first paint are already here; only later arrivals land with motion.
  const firstSeen = useRef<Set<string> | null>(null);
  if (firstSeen.current === null && presence.length > 0) firstSeen.current = new Set(presence.map((p) => String(p.user_id)));

  const isMe = (p: PresenceUser) => sameId(p.user_id, userId);
  const people = [...presence].sort((a, b) => {
    if (isMe(a) !== isMe(b)) return isMe(a) ? -1 : 1;
    return nameOf(a).localeCompare(nameOf(b));
  });
  if (people.length === 0 || (people.length === 1 && isMe(people[0]))) return null;

  const shown = people.length > MAX ? people.slice(0, MAX) : people;
  const extra = people.length - shown.length;
  const label = `Here now: ${people.map((p) => (isMe(p) ? `${nameOf(p)} (you)` : nameOf(p))).join(', ')}`;

  return (
    <div role="group" aria-label={label} title={label} className="flex flex-shrink-0 items-center">
      {shown.map((p, i) => (
        <span
          key={String(p.user_id)}
          aria-hidden="true"
          className={`${AVATAR} w-5 ${i > 0 ? '-ml-1.5' : ''} ${
            isMe(p) ? 'bg-surface-container-high text-on-surface-variant' : 'bg-surface-container-highest text-on-surface'
          } ${firstSeen.current?.has(String(p.user_id)) ? '' : 'motion-safe:animate-menuIn'}`}
        >
          {initialsOf(p)}
        </span>
      ))}
      <Appear
        show={extra > 0}
        className={`${AVATAR} -ml-1.5 min-w-[20px] bg-surface-container-high px-1 tabular-nums text-on-surface-variant`}
      >
        <span aria-hidden="true">+{extra}</span>
      </Appear>
    </div>
  );
};

export default PresenceRow;
